// frontend/src/theme/tokens.ts
// Single source of truth for token VALUES. Ported from docs/design-inventory/TOKENS.md
// + docs/DESIGN_BRIEF.md. Coral roles obey spec R1 (WCAG AA verified).
import type { PaletteMode } from "@mui/material/styles";

export const navy = {
  50: "#eef2f7", 100: "#d0dbe8", 200: "#a8bdd4", 300: "#7a9bbf", 400: "#4d7aab",
  500: "#1a365d", 600: "#152c4d", 700: "#10213a", 800: "#0d1f3c", 900: "#080f1e",
};
export const coral = {
  50: "#fef3f4", 100: "#fed7da", 200: "#fbb5ba", 300: "#f48d94", 400: "#ee6d7a",
  500: "#e35d6a", 600: "#c7404e", 700: "#a53441", 800: "#7f2530", 900: "#5a1a22",
};
export const green = {
  50: "#f0fff4", 100: "#c6f6d5", 200: "#9ae6b4", 300: "#68d391", 400: "#48bb78",
  500: "#38a169", 600: "#2f855a", 700: "#276749", 800: "#22543d", 900: "#1c4532",
};

// Per-product accent tokens — DEFINED, not applied (spec D2). DESIGN_BRIEF §2.
// Understated use only (chips/dots/icons/left-border) in a future per-product pass.
export const product = {
  mastery: "#6b46c1", transition: coral[500], portfolio: "#0d9488",
  resume: "#744210", interview: "#9f1239", linkedin: "#0a66c2", coach: "#2f855a",
} as const;

// Fixed ink/paper pair for components that are intentionally a dark, inverse
// panel regardless of the site's light/dark mode (e.g. ProblemCard) — NOT
// theme-reactive by design, so these can't be expressed as a `text.primary`/
// `background.default` sx path (which would flip in dark mode instead of
// staying constant). Same values as this file's light-mode theme colors,
// pulled out as named constants — see theme/index.ts's light-mode
// `text.primary`/`background.default`.
export const inverseInk = "#1a202c";
export const inversePaper = "#f7fafc";

// Coral resolved by role + mode (spec R1). Components use the MUI palette below,
// not these directly, but they are the source for it.
export function coralRoles(mode: PaletteMode) {
  const isDark = mode === "dark";
  return {
    text: isDark ? coral[500] : coral[600], // links/icons/active — AA as text in-mode
    textHover: isDark ? coral[400] : coral[700],
    ctaBg: coral[600], // contained-button bg, BOTH modes (white text 4.92:1)
    ctaBgHover: coral[700],
    fill: isDark ? coral[400] : coral[500], // progress/left-border/dots — decorative
  };
}

// Amber ink for the "Sold separately" product chip.
//
// WHY this is not `theme.palette.warning.dark`: that value is the CONSTANT
// "#b7791f" (see index.ts — only warning.main branches on isDark). Against this
// chip's own tinted amber ground it measures 3.33:1 in dark mode and 3.24:1 in
// light — both under the 4.5:1 AA floor, because the chip label is 10.5px and
// therefore "normal" text, not "large". These two measure 9.09:1 and 5.27:1.
//
// warning.dark is NOT fixed here: it has ~18 consumers across 6 other files and
// re-toning it globally is a separate change. Re-measure if the amber moves.
export function standaloneChipInk(mode: PaletteMode) {
  return mode === "dark" ? "#f6e05e" : "#8a5a10";
}

// Semantic tints — replace the ~15 ad-hoc alpha() values (spec §5.3, Task 6).
export function tints(mode: PaletteMode) {
  const isDark = mode === "dark";
  return {
    coralBg: isDark ? "rgba(238,109,122,0.10)" : "#fef7f6", // chip/selected bg
    selected: isDark ? "rgba(238,109,122,0.16)" : "rgba(227,93,106,0.08)",
    hover: isDark ? "#1c2128" : "#f9fafb",
    focusRing: isDark ? "rgba(238,109,122,0.30)" : "rgba(227,93,106,0.20)",
  };
}
